import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function DealerRegistrationPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    website: '',
    country: '',
    industry: '',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const countries = [
    'Deutschland', 'Nederland', 'België/Belgique', 'France', 'United Kingdom',
    'Österreich', 'Schweiz/Suisse', 'Luxembourg', 'Polska', 'Italia', 'España'
  ];

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = t('dealerRegistration:form.required');
    }
    if (!formData.contactPerson.trim()) {
      newErrors.contactPerson = t('dealerRegistration:form.required');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('dealerRegistration:form.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('dealerRegistration:form.invalidEmail');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('dealerRegistration:form.required');
    }
    if (!formData.country) {
      newErrors.country = t('dealerRegistration:form.required');
    }
    if (!formData.industry) {
      newErrors.industry = t('dealerRegistration:form.required');
    }
    if (!formData.message.trim()) {
      newErrors.message = t('dealerRegistration:form.required');
    }
    if (formData.message.length > 500) {
      newErrors.message = 'Maximum 500 characters allowed';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5v1k59r44f5krorl010', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          companyName: '',
          contactPerson: '',
          email: '',
          phone: '',
          website: '',
          country: '',
          industry: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-2 bg-amber-500/20 rounded-full mb-6">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-400">
              {t('dealerRegistration:hero.badge')}
            </span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('dealerRegistration:hero.title')}
          </h1>
          <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {t('dealerRegistration:hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('dealerRegistration:benefits.title')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-shadow text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                  <i className={`${t(`dealerRegistration:benefits.benefit${num}.icon`)} text-3xl text-amber-600`} style={{ width: '32px', height: '32px' }}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {t(`dealerRegistration:benefits.benefit${num}.title`)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t(`dealerRegistration:benefits.benefit${num}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('dealerRegistration:requirements.title')}
          </h2>
          <p className="text-lg text-gray-600 mb-10 text-center">
            {t('dealerRegistration:requirements.subtitle')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((num) => (
              <div key={num} className="flex items-start gap-4 bg-gray-50 rounded-xl p-6">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center">
                  <i className="ri-check-line text-white text-lg"></i>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {t(`dealerRegistration:requirements.req${num}`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t('dealerRegistration:form.title')}
            </h2>
            <p className="text-base text-gray-600">
              {t('dealerRegistration:form.subtitle')}
            </p>
          </div>

          <form id="dealer-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl p-10 shadow-lg">
            {submitStatus === 'success' && (
              <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800">{t('dealerRegistration:form.success')}</p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-800">{t('dealerRegistration:form.error')}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dealerRegistration:form.companyName')} *
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={t('dealerRegistration:form.companyNamePlaceholder')}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm ${
                    errors.companyName ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.companyName && <p className="mt-1 text-xs text-red-600">{errors.companyName}</p>}
              </div>

              <div>
                <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dealerRegistration:form.contactPerson')} *
                </label>
                <input
                  type="text"
                  id="contactPerson"
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  placeholder={t('dealerRegistration:form.contactPersonPlaceholder')}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm ${
                    errors.contactPerson ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.contactPerson && <p className="mt-1 text-xs text-red-600">{errors.contactPerson}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dealerRegistration:form.email')} *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('dealerRegistration:form.emailPlaceholder')}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dealerRegistration:form.phone')} *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t('dealerRegistration:form.phonePlaceholder')}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.phone && <p className="mt-1 text-xs text-red-600">{errors.phone}</p>}
              </div>
            </div>

            <div className="mb-6">
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                {t('dealerRegistration:form.website')}
              </label>
              <input
                type="url"
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder={t('dealerRegistration:form.websitePlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dealerRegistration:form.country')} *
                </label>
                <select
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm ${
                    errors.country ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('dealerRegistration:form.countryPlaceholder')}</option>
                  {countries.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
                {errors.country && <p className="mt-1 text-xs text-red-600">{errors.country}</p>}
              </div>

              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('dealerRegistration:form.industry')} *
                </label>
                <select
                  id="industry"
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm ${
                    errors.industry ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('dealerRegistration:form.industryPlaceholder')}</option>
                  <option value="fireplace">{t('dealerRegistration:form.industries.fireplace')}</option>
                  <option value="interior">{t('dealerRegistration:form.industries.interior')}</option>
                  <option value="construction">{t('dealerRegistration:form.industries.construction')}</option>
                  <option value="ecommerce">{t('dealerRegistration:form.industries.ecommerce')}</option>
                  <option value="other">{t('dealerRegistration:form.industries.other')}</option>
                </select>
                {errors.industry && <p className="mt-1 text-xs text-red-600">{errors.industry}</p>}
              </div>
            </div>

            <div className="mb-8">
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {t('dealerRegistration:form.message')} *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t('dealerRegistration:form.messagePlaceholder')}
                rows={6}
                maxLength={500}
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500 text-sm resize-none ${
                  errors.message ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              <div className="flex justify-between items-center mt-1">
                {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
                <p className="text-xs text-gray-500 ml-auto">{formData.message.length}/500</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-semibold rounded-full hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? t('dealerRegistration:form.submitting') : t('dealerRegistration:form.submit')}
            </button>
          </form>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('dealerRegistration:process.title')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500 flex items-center justify-center">
                  <span className="text-2xl font-bold text-black">{step}</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {t(`dealerRegistration:process.step${step}.title`)}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {t(`dealerRegistration:process.step${step}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            {t('dealerRegistration:cta.title')}
          </h2>

          <a href={`mailto:${t('dealerRegistration:cta.email')}`} className="inline-flex items-center gap-3 text-white hover:text-amber-400 transition-colors cursor-pointer">
            <i className="ri-mail-line text-2xl"></i>
            <div className="text-left">
              <div className="text-sm text-gray-400">Email</div>
              <div className="text-lg font-semibold">{t('dealerRegistration:cta.email')}</div>
            </div>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}